import {
  AdditionalInformationStateService
} from '../../../../core/services/impl/section-services/additional-information/additional-information-state.service';
import {FormConfig} from '../../../models/form-config';
import {FieldType} from '../../../models/field-type';
import {AdditionalInformationSectionConsts} from './additional-information-section.consts';
import {VehicleQuestionAnswer} from '../../../../core/consts/vehicle-question-answer.enum';
import {BeanConfig} from '../../../models/bean-config';
import {VehicleQuestionCode} from '../../../../core/consts/vehicle-question-code.enum';
import {
  PredefinedValuesService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {Validator} from '../../../models/validator.enum';
import {addYearsToDate} from '../../../utils/time-utils';
import {isNext} from '../../../../core/utils/utils';
import {RegExpKey, RegExpService} from '../../../services/reg-exp.service';
import {TestingPresetsService} from '../../../services/debugging/testing-presets.service';
import {TestingPresetKey} from '../../../../core/enums/debugging/testing-preset-key.enum';

export class AdditionalInformationSectionConfig {

  public consts = AdditionalInformationSectionConsts;
  isNext = isNext();

  private vinValidators = [
    Validator.REQUIRED_NOT_BLANK,
    {
      name: Validator.PATTERN,
      pattern: this.regExpService.getRegExp(RegExpKey.VIN)
    },
    {
      name: Validator.MAX_LENGTH,
      maxLength: 17
    },
    {
      name: Validator.MIN_LENGTH,
      minLength: 17
    }
  ];

  constructor(private stateService: AdditionalInformationStateService,
              private predefined: PredefinedValuesService,
              private regExpService: RegExpService) {
  }

  createBeanConfig(fieldKey: string): BeanConfig {
    return {
      propertyName: 'questions',
      keyPropertyName: 'questionCode',
      valuePropertyName: 'questionAnswer',
      keyPropertyValue: VehicleQuestionCode[fieldKey]
    };
  }

  createConfig(): FormConfig {
    let formConfig = <FormConfig>{
      formKey: 'ADDITIONAL_INFORMATION_SECTION',
      fieldsConfig: [
        {
          fieldKey: this.consts.KEYS_AMOUNT,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.KEYS_AMOUNT),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.KEYS_AMOUNT}`,
          type: FieldType.Select,
          description: 'ADDITIONAL_INFORMATION_SECTION.TOOLTIP.keysAmount',
          validators: [Validator.REQUIRED],
          options: {
            item: 'key',
            label: { label: 'label' },
            items: this.stateService.keysAmountDictionary.items
          },
          select: {
            dropdownPosition: 'bottom',
            multiple: false,
            searchable: true,
          }
        },
        {
          fieldKey: this.consts.ORIGINAL_KEYS_AMOUNT,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.ORIGINAL_KEYS_AMOUNT),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.ORIGINAL_KEYS_AMOUNT}`,
          type: FieldType.Select,
          validators: [Validator.REQUIRED],
          options: {
            item: 'key',
            label: { label: 'label' },
            items: this.stateService.keysAmountDictionary.items
          },
          select: {
            dropdownPosition: 'bottom',
            multiple: false,
            searchable: true,
          }
        },
        {
          fieldKey: this.consts.LEECHED_KEYS_AMOUNT,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.LEECHED_KEYS_AMOUNT),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.LEECHED_KEYS_AMOUNT}`,
          type: FieldType.Select,
          validators: [Validator.REQUIRED],
          options: {
            item: 'key',
            label: { label: 'label' },
            items: this.stateService.leechedKeysAmount.items
          },
          select: {
            dropdownPosition: 'bottom',
            multiple: false,
            searchable: true,
          }
        },
        {
          fieldKey: this.consts.TECHNICAL_INSPECTION_DATE,
          caption:  `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.TECHNICAL_INSPECTION_DATE}`,
          type: FieldType.DateField,
          validators: [Validator.REQUIRED],
          displayInEditableMode: true,
          materialDateFieldConfig: {
            minDate: addYearsToDate(-2, new Date()),
            maxDate: addYearsToDate(5, new Date())
          }
        },
        {
          fieldKey: this.consts.IMMOBILIZER,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.IMMOBILIZER),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.IMMOBILIZER}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            { value: VehicleQuestionAnswer.IMMOBILIZER_YES, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes' },
            { value: VehicleQuestionAnswer.IMMOBILIZER_NO, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no' },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.IMMOBILIZER_YES : undefined
        },
        {
          fieldKey: this.consts.MECHANIC_LOCK_TRANSMISSION,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.MECHANIC_LOCK_TRANSMISSION),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.MECHANIC_LOCK_TRANSMISSION}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            {
              value: VehicleQuestionAnswer.MECHANIC_LOCK_TRANSMISSION_YES,
              label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes'
            },
            {
              value: VehicleQuestionAnswer.MECHANIC_LOCK_TRANSMISSION_NO,
              label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no'
            },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.MECHANIC_LOCK_TRANSMISSION_YES : undefined
        },
        {
          fieldKey: this.consts.AUTOALARM,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.AUTOALARM),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.AUTOALARM}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            { value: VehicleQuestionAnswer.AUTOALARM_YES, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes' },
            { value: VehicleQuestionAnswer.AUTOALARM_NO, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no' },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.AUTOALARM_YES : undefined
        },
        {
          fieldKey: this.consts.GPS,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.GPS),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.GPS}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            { value: VehicleQuestionAnswer.GPS_YES, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes' },
            { value: VehicleQuestionAnswer.GPS_NO, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no' },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.GPS_YES : undefined
        },
        {
          fieldKey: this.consts.OTHER_SECURITIES,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.OTHER_SECURITIES),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.OTHER_SECURITIES}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            { value: VehicleQuestionAnswer.OTHER_SECURITIES_YES, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes' },
            { value: VehicleQuestionAnswer.OTHER_SECURITIES_NO, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no' },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.OTHER_SECURITIES_YES : undefined
        },
        {
          fieldKey: this.consts.USA_CANADA_REGISTRATION,
          fieldsDataFormat: 'objectArray',
          beanConfig: this.createBeanConfig(this.consts.USA_CANADA_REGISTRATION),
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.USA_CANADA_REGISTRATION}`,
          type: this.isNext ? FieldType.ButtonToggle : FieldType.Radio,
          validators: [Validator.REQUIRED],
          radioConfig: [
            { value: VehicleQuestionAnswer.USA_CANADA_REGISTRATION_YES, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.yes' },
            { value: VehicleQuestionAnswer.USA_CANADA_REGISTRATION_NO, label: 'VEHICLE_USE_SECTION.QUESTION_ANSWER.no' },
          ],
          initialValue: this.isNext ? VehicleQuestionAnswer.USA_CANADA_REGISTRATION_YES : undefined
        },
        {
          fieldKey: this.consts.VIN,
          caption: `ADDITIONAL_INFORMATION_SECTION.FORM_FIELDS.${this.consts.VIN}`,
          type: FieldType.TextField,
          validators: TestingPresetsService.isPresetActive(TestingPresetKey.VIN_VALIDATION_DISABLED) ? [] : this.vinValidators,
          properties: {
            textTransform: {
              type: 'uppercase'
            },
          }
        },
      ]
    };

    formConfig.fieldsConfig.forEach(fieldConfig => {
      this.predefined.addPredefinedValue(
        fieldConfig,
        this.predefined.values,
        formConfig.formKey,
        fieldConfig.fieldKey
      )
    });

    // leave configs only for visible fields
    formConfig.fieldsConfig = formConfig.fieldsConfig.filter(fieldConfig => this.stateService.isVisible(fieldConfig.fieldKey));

    return formConfig;
  }
}

